import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { LoadingWrapper } from 'src/domains/layouts/shared/loaders';
import { HeroWidgetState } from 'src/domains/layouts/webview/components/heroEvents/heroWidget/HeroWidget.state';
import { HeroWidgetModelType } from 'src/api/config/cms_new/hero_widget/get_hero_widget_active';
import { HeroWidgetRacing } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetRacing';
import { HomepageHeroWidgetNFL } from 'src/domains/layouts/webview/components/heroEvents/nfl/HomepageHeroWidgetNFL';
import { HomepageHeroWidgetDefault } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetDefault';
import { HeroWidgetWrapper } from 'src/domains/layouts/webview/components/heroEvents/HomepageHeroWidget.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';

type HeroWidgetItemRendererPropsType = {
    heroWidgetItem: HeroWidgetModelType;
};

const HeroWidgetItemRenderer = observer(
    'HeroWidgetItemRenderer',
    ({ heroWidgetItem }: HeroWidgetItemRendererPropsType): JSX.Element | null => {
        const { event_id, market_id } = heroWidgetItem;
        const eventModel = event_id.getEventModel();
        if (eventModel === null) {
            return null;
        }
        const marketModel = market_id.getModel();
        if (marketModel === null) {
            return null;
        }

        switch (eventModel.sport) {
            case 'horseracing':
            case 'greyhoundracing':
                return <HeroWidgetRacing heroWidgetItem={heroWidgetItem} />;
            case 'americanfootball':
                return (
                    <HomepageHeroWidgetNFL
                        heroWidgetItem={heroWidgetItem}
                        smallEvent={eventModel.convertToSmallEvent()}
                    />
                );
            default:
                return <HomepageHeroWidgetDefault heroWidgetItem={heroWidgetItem} />;
        }
    }
);

export const HeroWidget = observer('HeroWidget', (): JSX.Element | null => {
    const [state] = useState(() => new HeroWidgetState());
    const common = useCommon();
    const appLayoutsState = AppLayoutsState.get(common);
    const configState = ConfigComponents.get(common).unifiedComponentConfig;
    const { sportsbookVariant } = configState.componentVariants;
    const { heroWidgetListForView } = appLayoutsState.heroWidgetState;

    if (heroWidgetListForView.length === 0 && sportsbookVariant === DefaultComponentVariantType.SECONDARY) {
        return null;
    }

    return (
        <LoadingWrapper
            computedHeight={state.elementRefHeight}
            data-test='hero-widget'
        >
            <HeroWidgetWrapper
                ref={state.reactiveWrapper.setRef}
                variant={sportsbookVariant}
            >
                {heroWidgetListForView.map((item) => {
                    return (
                        <HeroWidgetItemRenderer
                            key={item.event_id.toOldId()}
                            heroWidgetItem={item}
                        />
                    );
                })}
            </HeroWidgetWrapper>
        </LoadingWrapper>
    );
});
